import { supabase } from './supabase';

export interface BuddyMsg { role: 'user' | 'assistant'; content: string; suggestions?: string[] }

export interface BuddyContext {
  partnerName: string;
  platform: string;
  existingQuestions: string[];
}

// Must match VOICES in supabase/functions/text-to-voice/index.ts
export const VOICES = [
  { id: 'Kore',   label: 'Calm woman' },
  { id: 'Aoede',  label: 'Breezy woman' },
  { id: 'Puck',   label: 'Upbeat man' },
  { id: 'Charon', label: 'Deep man' },
] as const;
export type VoiceId = typeof VOICES[number]['id'];

export const MAX_VOICE_CHARS = 300;

// Must match SPEECH_LANGUAGES in supabase/functions/_shared/buddy.ts.
// 'none' = speak exactly what was typed (no translation).
export interface LanguageOption { id: string; label: string; alt?: string }
// 'none' first, then every language Gemini TTS can speak, A to Z. `alt` = extra words the search matches.
export const LANGUAGES: readonly LanguageOption[] = [
  { id: 'none', label: 'As I typed' },
  { id: 'acehnese_arabic_script', label: 'Acehnese (Arabic script)' },
  { id: 'afrikaans', label: 'Afrikaans' },
  { id: 'albanian', label: 'Albanian', alt: 'tosk' },
  { id: 'amharic', label: 'Amharic' },
  { id: 'arabic', label: 'Arabic' },
  { id: 'arabic_latin_letters', label: 'Arabic (Latin letters)', alt: 'romanized arabizi' },
  { id: 'armenian', label: 'Armenian' },
  { id: 'assamese', label: 'Assamese' },
  { id: 'awadhi', label: 'Awadhi' },
  { id: 'azerbaijani_north', label: 'Azerbaijani (North)', alt: 'azeri' },
  { id: 'azerbaijani_south', label: 'Azerbaijani (South)', alt: 'azeri torki' },
  { id: 'balinese', label: 'Balinese' },
  { id: 'bangla_bengali', label: 'Bangla (Bengali)', alt: 'bengali' },
  { id: 'banjar_arabic_script', label: 'Banjar (Arabic script)' },
  { id: 'banjar_latin_script', label: 'Banjar (Latin script)' },
  { id: 'bashkir', label: 'Bashkir' },
  { id: 'basque', label: 'Basque' },
  { id: 'belarusian', label: 'Belarusian' },
  { id: 'bemba', label: 'Bemba' },
  { id: 'bhojpuri', label: 'Bhojpuri' },
  { id: 'bosnian', label: 'Bosnian' },
  { id: 'buginese', label: 'Buginese' },
  { id: 'bulgarian', label: 'Bulgarian' },
  { id: 'burmese', label: 'Burmese' },
  { id: 'cantonese', label: 'Cantonese' },
  { id: 'catalan', label: 'Catalan' },
  { id: 'cebuano', label: 'Cebuano' },
  { id: 'chhattisgarhi', label: 'Chhattisgarhi' },
  { id: 'chinese_simplified', label: 'Chinese (Simplified)', alt: 'mandarin hans' },
  { id: 'chinese_traditional', label: 'Chinese (Traditional)', alt: 'mandarin hant' },
  { id: 'crimean_tatar', label: 'Crimean Tatar' },
  { id: 'croatian', label: 'Croatian' },
  { id: 'czech', label: 'Czech' },
  { id: 'danish', label: 'Danish' },
  { id: 'dutch', label: 'Dutch' },
  { id: 'dyula_jula', label: 'Dyula (Jula)', alt: 'jula dioula' },
  { id: 'dzongkha', label: 'Dzongkha' },
  { id: 'egyptian_arabic', label: 'Egyptian Arabic' },
  { id: 'english', label: 'English' },
  { id: 'estonian', label: 'Estonian' },
  { id: 'filipino_tagalog', label: 'Filipino (Tagalog)', alt: 'tagalog philippines' },
  { id: 'finnish', label: 'Finnish' },
  { id: 'french', label: 'French' },
  { id: 'fulfulde_nigeria', label: 'Fulfulde (Nigeria)', alt: 'fula fulani pulaar' },
  { id: 'galician', label: 'Galician' },
  { id: 'georgian', label: 'Georgian' },
  { id: 'german', label: 'German' },
  { id: 'greek', label: 'Greek' },
  { id: 'guarani', label: 'Guarani' },
  { id: 'gujarati', label: 'Gujarati' },
  { id: 'haitian_creole', label: 'Haitian Creole' },
  { id: 'hausa', label: 'Hausa', alt: 'nigeria niger ghana' },
  { id: 'hebrew', label: 'Hebrew' },
  { id: 'hindi', label: 'Hindi' },
  { id: 'hungarian', label: 'Hungarian' },
  { id: 'icelandic', label: 'Icelandic' },
  { id: 'igbo', label: 'Igbo', alt: 'nigeria' },
  { id: 'iloko', label: 'Iloko' },
  { id: 'indonesian', label: 'Indonesian' },
  { id: 'italian', label: 'Italian' },
  { id: 'japanese', label: 'Japanese' },
  { id: 'javanese', label: 'Javanese' },
  { id: 'kabyle', label: 'Kabyle', alt: 'berber' },
  { id: 'kamba', label: 'Kamba' },
  { id: 'kannada', label: 'Kannada' },
  { id: 'kashmiri_arabic_script', label: 'Kashmiri (Arabic script)' },
  { id: 'kashmiri_devanagari', label: 'Kashmiri (Devanagari)' },
  { id: 'kazakh', label: 'Kazakh' },
  { id: 'khmer', label: 'Khmer' },
  { id: 'kikuyu', label: 'Kikuyu' },
  { id: 'kinyarwanda', label: 'Kinyarwanda' },
  { id: 'kongo', label: 'Kongo' },
  { id: 'korean', label: 'Korean' },
  { id: 'kurdish_sorani', label: 'Kurdish (Sorani)', alt: 'central' },
  { id: 'kyrgyz', label: 'Kyrgyz' },
  { id: 'lao', label: 'Lao' },
  { id: 'latgalian', label: 'Latgalian' },
  { id: 'latvian', label: 'Latvian' },
  { id: 'lingala', label: 'Lingala' },
  { id: 'lithuanian', label: 'Lithuanian' },
  { id: 'luganda_ganda', label: 'Luganda (Ganda)', alt: 'uganda' },
  { id: 'luxembourgish', label: 'Luxembourgish' },
  { id: 'macedonian', label: 'Macedonian' },
  { id: 'magahi', label: 'Magahi' },
  { id: 'maithili', label: 'Maithili' },
  { id: 'malay', label: 'Malay', alt: 'malaysia' },
  { id: 'malayalam', label: 'Malayalam' },
  { id: 'maltese', label: 'Maltese' },
  { id: 'manipuri_meitei', label: 'Manipuri (Meitei)', alt: 'meitei' },
  { id: 'marathi', label: 'Marathi' },
  { id: 'minangkabau_arabic_script', label: 'Minangkabau (Arabic script)' },
  { id: 'minangkabau_latin_script', label: 'Minangkabau (Latin script)' },
  { id: 'mizo', label: 'Mizo' },
  { id: 'mongolian', label: 'Mongolian', alt: 'halh' },
  { id: 'nepali', label: 'Nepali' },
  { id: 'norwegian_bokm_l', label: 'Norwegian Bokmål' },
  { id: 'norwegian_nynorsk', label: 'Norwegian Nynorsk' },
  { id: 'nyanja_chichewa', label: 'Nyanja (Chichewa)', alt: 'chichewa malawi' },
  { id: 'occitan', label: 'Occitan' },
  { id: 'odia', label: 'Odia', alt: 'oriya' },
  { id: 'pangasinan', label: 'Pangasinan' },
  { id: 'pashto', label: 'Pashto', alt: 'pushto pukhto' },
  { id: 'persian_dari_afghanistan', label: 'Persian (Dari, Afghanistan)', alt: 'farsi dari' },
  { id: 'persian_iran', label: 'Persian (Iran)', alt: 'farsi iranian' },
  { id: 'polish', label: 'Polish' },
  { id: 'portuguese', label: 'Portuguese' },
  { id: 'punjabi', label: 'Punjabi', alt: 'panjabi' },
  { id: 'romanian', label: 'Romanian' },
  { id: 'russian', label: 'Russian' },
  { id: 'santali', label: 'Santali' },
  { id: 'sepedi_northern_sotho', label: 'Sepedi (Northern Sotho)', alt: 'sepedi south africa' },
  { id: 'serbian', label: 'Serbian' },
  { id: 'sesotho_southern_sotho', label: 'Sesotho (Southern Sotho)', alt: 'sesotho lesotho' },
  { id: 'sindhi', label: 'Sindhi' },
  { id: 'sinhala', label: 'Sinhala' },
  { id: 'slovak', label: 'Slovak' },
  { id: 'slovenian', label: 'Slovenian' },
  { id: 'somali', label: 'Somali' },
  { id: 'spanish', label: 'Spanish' },
  { id: 'swahili', label: 'Swahili', alt: 'kiswahili' },
  { id: 'swati', label: 'Swati' },
  { id: 'swedish', label: 'Swedish' },
  { id: 'tajik', label: 'Tajik' },
  { id: 'tamil', label: 'Tamil' },
  { id: 'telugu', label: 'Telugu' },
  { id: 'thai', label: 'Thai' },
  { id: 'tigrinya', label: 'Tigrinya' },
  { id: 'turkish', label: 'Turkish' },
  { id: 'twi', label: 'Twi (Akan)', alt: 'fante ashanti asante akuapem ghana' },
  { id: 'uyghur', label: 'Uyghur', alt: 'uighur' },
  { id: 'uzbek', label: 'Uzbek', alt: 'northern' },
  { id: 'vietnamese', label: 'Vietnamese' },
];
export type LanguageId = string;
// Shown as quick chips; everything else is behind "More languages".
export const POPULAR_LANGUAGE_IDS: LanguageId[] = ['twi', 'hausa', 'french', 'swahili', 'igbo', 'arabic', 'spanish', 'portuguese'];

async function errorCode(error: any): Promise<string> {
  try {
    const body = await error?.context?.json?.();
    if (body?.error) return body.error;
  } catch { /* fall through */ }
  return 'server_error';
}

export async function askBuddy(
  messages: BuddyMsg[],
  context: BuddyContext,
): Promise<{ reply: string; suggestions: string[] } | { error: string }> {
  const { data, error } = await supabase.functions.invoke('question-buddy', {
    body: { messages: messages.map(({ role, content }) => ({ role, content })), context },
  });
  if (error || !data?.reply) return { error: await errorCode(error) };
  return { reply: data.reply, suggestions: data.suggestions ?? [] };
}

// Turns text into a spoken clip. Returns a playable .wav blob.
// If `language` isn't 'none', the text is translated into it first; `spokenText`
// is then the translation that was actually spoken.
export async function textToVoice(
  text: string,
  voice: VoiceId,
  language: LanguageId = 'none',
): Promise<{ blob: Blob; seconds: number; spokenText?: string } | { error: string }> {
  const { data, error } = await supabase.functions.invoke('text-to-voice', { body: { text, voice, language } });
  if (error || !data?.wav_base64) return { error: await errorCode(error) };
  const bytes = Uint8Array.from(atob(data.wav_base64), c => c.charCodeAt(0));
  return { blob: new Blob([bytes], { type: 'audio/wav' }), seconds: data.seconds ?? 0, spokenText: data.spoken_text ?? undefined };
}

// Same bucket + folder pattern as VoiceRecorder, so playback for the recipient
// and the automatic audio cleanup work exactly the same.
export async function uploadQuestionVoice(questionId: string, blob: Blob): Promise<string> {
  const fileName = `questions/${questionId}/${Date.now()}.wav`;
  const { error } = await supabase.storage.from('voice-notes').upload(fileName, blob, { contentType: 'audio/wav' });
  if (error) throw error;
  return supabase.storage.from('voice-notes').getPublicUrl(fileName).data.publicUrl;
}

export const BUDDY_ERRORS: Record<string, string> = {
  limit: "You've used all your Shennel chats for today. Come back tomorrow!",
  sign_in_required: 'Create a free account (just tap Send) to turn text into a voice note.',
  bad_text: 'Keep it between 2 and 300 characters.',
  translate_failed: "Couldn't translate that right now. Please try again.",
  server_error: 'Shennel is having a moment. Please try again.',
};
